/**
 * Status **peer** sections for the Entity Details panel — the level ADR-039 §D-1
 * inserted between the component and the interface (ADR-024 §4 amended
 * 2026-08-05; REQ-STATUS-086; feature-spec §5.2).
 *
 * Selecting a peer icon on the Peer Manager's stack lands here, and this pane
 * carries **three levels**: the peer's own rollup and vitals, its interfaces,
 * and each interface's connections. An operator who has just stopped a peer
 * needs to see what stopped with it; a pane showing only the peer's own rollup
 * would send them back to the configuration surface to find out.
 *
 * # Composed from reads that already exist — no new projection
 *
 * A peer's status report *does* nest its interfaces and connections, and the
 * obvious implementation is to read that nesting back. It is not available: the
 * collector consumes it in ``_rollup.py`` to compute the four-level rollup and
 * stores **one** ``component_samples`` row. There is no ``status.interface_samples``
 * table, and ``ConnectionStatus.interface_id`` — the field that carries the
 * grouping on the wire — is used for the rollup and never persisted. So this
 * pane assembles from four reads that all predate it:
 *
 *   - the **peer's rollup and vitals** from the live status feed, under the
 *     namespaced ``peer:<name>`` component id (:func:`peerComponentId`);
 *   - the **interface→peer grouping** from ``GET /api/admin/peers/{peer_id}/interfaces``
 *     (REQ-PEER-078) — the one nested read, and the reason a *non-privileged*
 *     session can group at all, since interface→peer parentage is privileged as
 *     a field (feature-spec §2.10) while the path supplies it to everyone;
 *   - each **interface's health** from that same status feed, where a direct
 *     peer interface reports with ``component_id == config.name``;
 *   - each **interface's connections** from the per-component connections read
 *     (REQ-STATUS-021), which is what the interface's own pane already uses.
 *
 * # Connections load on expand, not on open
 *
 * The connections read is per **interface**, because the persisted rows carry no
 * interface attribution to split a peer-level read by. Issuing all of them when
 * the pane opens would put up to five round trips (REQ-PEER-071's per-peer cap)
 * behind a click that may only have been meant to check a state pill. Each
 * interface row is therefore collapsible and fetches when expanded — the same
 * shape REQ-STATUS-044 already established for connection rosters, and the same
 * principle REQ-STATUS-072 applies to trends.
 *
 * # The left join runs at every level, and always in the same direction
 *
 * Existence comes from configuration and health from the feed, never the other
 * way round (feature-spec §2.2). An interface the operator created a moment ago
 * is listed at once, as honestly ``unknown``; it is never absent and never green
 * (REQ-STATUS-016 / -038).
 *
 * Cross-references:
 *   - REQ-STATUS-086 (this pane), -082 / -083 / -085 (the rollup it renders),
 *     -084 (``stopped`` distinct from ``standby``), -087 (no connections).
 *   - REQ-PEER-064 (a peer stop is non-destructive), -078 (the read field-set).
 *   - ADR-024 §4 (one pane, sticky headers, no sub-tabs); REQ-UI-046, REQ-UI-059.
 */
import { useState } from "react";
import { CaretLeft, CaretDown, CaretRight } from "@phosphor-icons/react";
import { type ResolvedComponent, type RollupState } from "@/system-status/status-model";
import { peerComponentId, rollupDisplayRank } from "@/system-status/status-topology";
import {
  MiniStateRow,
  StatusPill,
  StatusSectionsProvider,
} from "@/system-status/status-detail-primitives";
import { ConnectionList } from "@/system-status/status-connection-list";
import type { StatusConnection } from "@/system-status/status-model";
import { StatusConnectionSections } from "@/entity-details/status-connection-sections";
import { SectionShell, SectionRow } from "@/entity-details/section-shell";
import { ComponentVitalsSections } from "@/entity-details/status-component-sections";
import { StatusTrendSection } from "@/entity-details/status-trend-section";
import {
  interfaceComponentId,
  type PeerInterfaceRow,
} from "@/entity-details/status-detail-scoping";
import type { PeerView } from "@/peers/peer-model";
import {
  ConfirmStrip,
  InterfaceActionButtons,
  InterfaceActionConfirm,
  type PeerActionKind,
} from "@/entity-details/status-peer-actions";

/** Props for :func:`StatusPeerSections`. */
export interface StatusPeerSectionsProps {
  /** The peer's operator label — the selection's identity. */
  peerName: string;
  /** The configured peer, or null when the roster does not list it. */
  configured: PeerView | null;
  /** The peer's own reporting component, or undefined when it has not reported. */
  component: ResolvedComponent | undefined;
  /** The peer's rollup, resolved by the panel wrapper (shared with the header pill). */
  state: RollupState;
  /** This peer's interfaces, health already joined (:func:`joinPeerInterfaces`). */
  interfaces: readonly PeerInterfaceRow[];
  /** Whether the interface list is still loading. */
  interfacesLoading: boolean;
  /** The interface list's error, when it failed. */
  interfacesError: Error | null;
  /** Display name of the hosting node, for the back breadcrumb (Flow L §9). */
  parentLabel: string;
  /** Step the selection up to the hosting ``status-component``. */
  onBack: () => void;
  /** Drill into one interface → a ``status-interface`` focus carrying this peer. */
  onSelectInterface: (interfaceName: string) => void;
  /**
   * The bounded mutation subset (REQ-UI-154), or ``undefined`` for a session
   * that may not mutate.
   *
   * Optional as a whole rather than per-control: "may this session change
   * anything here" is one question, and threading five nullable callbacks would
   * let a future caller supply four of them and produce a half-enabled pane.
   * Absent means the controls are absent — never disabled (REQ-UI-143).
   */
  actions?: PeerPaneActions;
}

/** The acts the Peer pane offers a privileged session (REQ-UI-154). */
export interface PeerPaneActions {
  onSetInterfaceEnabled: (interfaceId: string, enabled: boolean) => void;
  onDeleteInterface: (interfaceId: string) => void;
  onDeletePeer: () => void;
  onExportPeer: () => void;
  /** Open the full configuration surface — everything beyond this subset. */
  onConfigure: () => void;
  exportPending?: boolean;
}

/**
 * Render the peer detail body: a back breadcrumb to the Peer Manager, the peer's
 * identity and vitals, and its interfaces each disclosing their connections.
 */
export function StatusPeerSections({
  peerName,
  configured,
  component,
  state,
  interfaces,
  interfacesLoading,
  interfacesError,
  parentLabel,
  onBack,
  onSelectInterface,
  actions,
}: StatusPeerSectionsProps): React.ReactElement {
  // Held at the pane so only one peer-level confirmation can be armed at a time.
  const [confirmDeletePeer, setConfirmDeletePeer] = useState(false);
  // A peer with no interfaces never reports at all — `meridian_peer_manager`
  // groups its reports over the resolved interfaces — so "configured with zero"
  // and "has some, none reported" both arrive here as `component === undefined`
  // and must not read alike. Only the first is a fault (REQ-STATUS-087's rule one
  // level up), and `interface_count` is what tells them apart (REQ-UI-140).
  const noInterfaces = configured?.interface_count === 0;
  const started = interfaces.filter((row) => row.config.state !== "stopped").length;

  return (
    <StatusSectionsProvider>
      <div className="border-b border-border px-4 py-2">
        <button
          type="button"
          onClick={onBack}
          data-testid="peer-back"
          className="flex items-center gap-1 rounded text-meta text-text-secondary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          <CaretLeft size={12} weight="bold" aria-hidden="true" />
          {parentLabel}
        </button>
      </div>

      <SectionShell id="status-identity" title="Identity">
        <SectionRow label="Peer" value={<span className="font-mono">{peerName}</span>} />
        {/* The namespaced id is shown because it is what an operator reading a
            dashboard or a log line will see, and `peer:cds` beside an interface
            called `cds` is the moment the namespace stops looking like noise. */}
        <SectionRow
          label="Component id"
          value={<span className="font-mono">{peerComponentId(peerName)}</span>}
        />
        <SectionRow label="Parent component" value={parentLabel} />
        <SectionRow
          label="Interfaces"
          value={
            configured?.interface_count === undefined
              ? "Not counted"
              : `${started} started of ${interfaces.length}`
          }
        />
      </SectionShell>

      <SectionShell id="status-health" title="Health rollup">
        <MiniStateRow
          testId="rollup-input-serving"
          label="Serving"
          state={state}
          detail={servingText(state, noInterfaces)}
          description="Indicates whether or not this peer is receiving data from Meridian."
        />
      </SectionShell>

      {/* The peer's own vitals, when it reports them. A peer report carries domain
          metrics (`peer_report_domain`) but no process resource block of its own —
          it is not a process, the Peer Manager is — so the shared vitals block
          renders whichever of those the report actually carries rather than
          fabricating rows. */}
      {component !== undefined && (
        <ComponentVitalsSections component={component} nodeState={state} />
      )}

      <SectionShell id="status-peer-interfaces" title="Interfaces and connections">
        {interfacesLoading ? (
          <p role="status" className="text-body-sm text-text-secondary">
            Loading interfaces…
          </p>
        ) : interfacesError !== null ? (
          // Honest failure, never an empty list that reads as "none configured"
          // (CLAUDE.md §8.1) — an operator must not conclude a flow was deleted
          // because its roster failed to load.
          <p
            role="alert"
            className="text-body-sm text-destructive"
            data-testid="peer-interfaces-error"
          >
            Could not load this peer&rsquo;s interfaces: {interfacesError.message}
          </p>
        ) : interfaces.length === 0 ? (
          <p className="text-body-sm text-text-muted" data-testid="peer-interfaces-empty">
            No interfaces are configured. This peer has no path to send on, so nothing reaches it.
          </p>
        ) : (
          <ul data-testid="peer-interfaces" className="flex flex-col gap-1">
            {[...interfaces]
              // Worst first, unconditionally: a list has no cap to hide anything
              // behind, so there is nothing here for configured order to protect.
              .sort((a, b) => rollupDisplayRank(a.state) - rollupDisplayRank(b.state))
              .map((row) => (
                <PeerInterfaceEntry
                  key={row.config.interface_id}
                  row={row}
                  peerName={peerName}
                  onSelect={() => onSelectInterface(row.config.name)}
                  actions={actions}
                />
              ))}
          </ul>
        )}
      </SectionShell>

      {/* REQ-UI-154's peer level. Export sits apart from Delete because one of
          them takes a copy OUT of the deployment and the other empties it, and a
          row that puts them side by side invites the wrong click. */}
      {actions !== undefined && (
        <SectionShell id="status-peer-manage" title="Manage">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={actions.onConfigure}
              data-testid="peer-manage-configure"
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            >
              Configure in Peers&hellip;
            </button>
            <button
              type="button"
              onClick={actions.onExportPeer}
              disabled={actions.exportPending === true}
              data-testid="peer-manage-export"
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated disabled:opacity-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            >
              {actions.exportPending === true ? "Exporting…" : "Export…"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDeletePeer(true)}
              data-testid="peer-manage-delete"
              className="rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            >
              Delete peer&hellip;
            </button>
          </div>
          {confirmDeletePeer && (
            <ConfirmStrip
              testId="peer-manage-confirm-delete"
              danger
              confirmLabel="Delete"
              message={
                <>
                  Delete peer <strong>{peerName}</strong>
                  {configured?.interface_count !== undefined
                    ? ` and its ${configured.interface_count} ${configured.interface_count === 1 ? "interface" : "interfaces"}`
                    : " and every interface beneath it"}
                  ? Every connection beneath them goes too. Data already queued is not redirected.
                  This cannot be undone.
                </>
              }
              onConfirm={() => {
                setConfirmDeletePeer(false);
                actions.onDeletePeer();
              }}
              onCancel={() => setConfirmDeletePeer(false)}
            />
          )}
          <p className="pt-1 text-meta text-text-muted">
            A subset: enable or disable an interface, delete at any of the three levels, and export
            this peer as a bundle. Anything larger — adding an interface, editing a filter,
            installing trust material — opens the peer configuration surface. Export omits private
            key references; the bundle says so in their place.
          </p>
        </SectionShell>
      )}

      {component !== undefined && (
        <StatusTrendSection
          componentId={component.component_id}
          componentKind={component.component_kind}
          entityLabel={peerName}
          domainOrder={Object.keys(component.domain ?? {})}
        />
      )}
    </StatusSectionsProvider>
  );
}

/**
 * One interface row: its own state, a disclosure for its connections, and a
 * control that promotes it to its own selection.
 *
 * The two affordances are deliberately separate. Expanding answers *"what is
 * under this"* without leaving the peer; selecting answers *"show me this one"*
 * and moves the bus. Collapsing them into one control would make every glance at
 * a connection count cost the operator their place in the peer.
 */
function PeerInterfaceEntry({
  row,
  peerName,
  onSelect,
  actions,
}: {
  row: PeerInterfaceRow;
  peerName: string;
  onSelect: () => void;
  actions?: PeerPaneActions;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState<PeerActionKind | null>(null);
  /**
   * Which component this interface's connections are read from, and whether the
   * rows need splitting back out by interface.
   *
   * Resolved ONCE and used by both the roster and the drawer. They read the same
   * per-component route, so letting them derive it separately is how the roster
   * lists a connection whose drawer then fetches history for a component that
   * does not exist — an empty drawer on a row that is plainly there.
   */
  const reportsForItself = row.component !== undefined;
  const connectionsComponentId = reportsForItself
    ? interfaceComponentId(row.config.name)
    : peerComponentId(peerName);
  // REQ-PEER-064: a peer stop suppresses its interfaces without overwriting their
  // recorded states. When the two disagree the pane says so — showing only the
  // rollup would imply this interface was individually stopped, so an operator
  // restarting the peer would expect it to stay off.
  const suppressed = row.state === "stopped" && row.config.state !== "stopped";

  return (
    <li className="flex flex-col">
      <div className="flex items-center gap-2 text-body-sm">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label={`${expanded ? "Hide" : "Show"} connections for ${row.config.name}`}
          data-testid={`peer-iface-toggle-${row.config.name}`}
          className="flex-none rounded p-0.5 text-text-secondary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          {expanded ? (
            <CaretDown size={12} weight="bold" aria-hidden="true" />
          ) : (
            <CaretRight size={12} weight="bold" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          onClick={onSelect}
          data-testid={`peer-iface-row-${row.config.name}`}
          title={row.config.name}
          className="min-w-0 flex-1 truncate rounded px-1 text-left font-mono text-text-primary hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          {row.config.name}
        </button>
        <StatusPill state={row.state} />
        {actions !== undefined && <InterfaceActionButtons view={row.config} onAsk={setPending} />}
      </div>
      {/* Below the row, never inside it — the row is a flex line and a strip
          placed in it is squeezed to a sliver beside the pill. */}
      {actions !== undefined && (
        <InterfaceActionConfirm
          view={row.config}
          pending={pending}
          onSetEnabled={(enabled) =>
            actions.onSetInterfaceEnabled(row.config.interface_id, enabled)
          }
          onDelete={() => actions.onDeleteInterface(row.config.interface_id)}
          onCancel={() => setPending(null)}
        />
      )}
      {/* The type sits BELOW the name, not beside it, and that is a measurement
          rather than a taste call. On one row at the panel's real width the
          toggle, the type and the pill are all `flex-none` and reserve 171 of
          250 px between them, leaving the *name* — the thing the operator is
          looking for — 55 px against the 58 it needs. `sapient` rendered as
          `sapie…`. The primary identifier must not be the element that yields,
          and a flex tuned to fit one width would only move the failure to
          another (ADR-024's minimum panel width). Found by the §5.11 live pass;
          no unit test could have seen it, because jsdom lays nothing out. */}
      <p className="pl-6 font-mono text-meta text-text-muted">{row.config.type_id}</p>
      {suppressed && (
        <p
          className="pl-6 text-meta text-text-muted"
          data-testid={`peer-iface-suppressed-${row.config.name}`}
        >
          Stopped by peer {peerName} — its own state is {row.config.state} and is preserved.
        </p>
      )}
      {/* ⚠ A non-reporting interface's connections come from the PEER's report.
          `meridian_peer_manager` emits no per-interface status for the CDS types
          (issue #754), so `interfaceComponentId(name)` names a component that does
          not exist and the roster came back EMPTY — reading as "no connections
          configured" on a peer with four live slots per interface. That is the
          REQ-STATUS-016 false-green shape, and it is what this pane's own promise
          ("each interface's connections") was failing to keep.

          The peer's own report carries every interface's connections in one flat
          list, and `restrictToInterface` splits them back out on the
          `connection_id` prefix the emitter writes. When #754 lands and these
          interfaces report for themselves, the first branch starts winning on its
          own and this fallback stops firing — no change needed here. */}
      {expanded && (
        <div className="pl-6 pt-1">
          {!reportsForItself && (
            <p
              className="pb-1 text-meta text-text-muted"
              data-testid={`peer-conn-source-${row.config.name}`}
            >
              This interface type publishes no status of its own, so its connections are read from{" "}
              {peerName}&rsquo;s own report.
            </p>
          )}
          <ConnectionList
            componentId={connectionsComponentId}
            restrictToInterface={reportsForItself ? undefined : row.config.name}
            height={200}
            // REQ-UI-155 — a LIVE DEFECT against REQ-STATUS-086, not a new
            // feature. This pane listed each interface's connections and passed
            // no `renderDrawer`, so every row was inert: an operator who came
            // here to find out why a peer was degraded had to leave for the
            // interface pane to read a single row and then come back. The
            // interface pane's own roster has had this drawer since
            // REQ-STATUS-044; only this copy of the list lacked it.
            //
            // Expanding is NOT selecting. ADR-024 §5's one-focus contract is a
            // property of the selection bus, and a drawer is a disclosure within
            // the pane — exactly as it is one level down. Promoting the
            // connection instead would cost the operator their place in the peer
            // every time they glanced at a row.
            renderDrawer={(connection: StatusConnection) => (
              <StatusConnectionSections
                connection={connection}
                componentId={connectionsComponentId}
              />
            )}
          />
        </div>
      )}
    </li>
  );
}

/**
 * The operator-facing sentence behind a peer's "Serving" row.
 *
 * ``stopped`` and ``standby`` share a colour and must never share a sentence:
 * one says an operator turned this off, the other says another replica is
 * carrying it, and they make opposite claims about whether anyone needs to act
 * (REQ-STATUS-084).
 */
function servingText(state: RollupState, noInterfaces: boolean): string {
  switch (state) {
    case "stopped":
      return "Not serving — this peer was stopped by an operator";
    case "standby":
      return "Not serving — another replica holds the lease";
    case "unknown":
      return "Not reported — no status sample for this peer yet";
    case "unhealthy":
      return noInterfaces
        ? "Not serving — no interfaces are configured, so it has no path to send on"
        : "Not serving — see the interfaces below";
    case "degraded":
      return "Serving, degraded — see the interfaces below";
    default:
      return "Delivering";
  }
}
